import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import { logError } from 'lib/logging'
import { useSendingDomains, type SendingDomainRow } from './useSendingDomains'

const STATUS_COLOR: Record<string, BadgeColor> = {
  verified: `green`,
  pending: `amber`,
  failed: `red`,
}

function DnsRecords({ domain }: { domain: SendingDomainRow }): ReactElement | null {
  if (!domain.dnsRecords?.length) return null
  return (
    <div className="mt-2 rounded-md border border-default bg-surface p-3">
      <p className="mb-2 text-sm text-subtle">
        Publish these DNS records with your domain host, then check verification. A record shows as Found once it
        resolves at your DNS.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {domain.dnsRecords.map((r) => (
            <TableRow key={`${r.type}-${r.name}`}>
              <TableCell className="font-mono text-xs">{r.type}</TableCell>
              <TableCell className="font-mono text-xs break-all">{r.name}</TableCell>
              <TableCell className="font-mono text-xs break-all">{r.value}</TableCell>
              <TableCell>
                <Badge
                  label={r.found == null ? `Not checked` : r.found ? `Found` : `Missing`}
                  color={r.found == null ? `gray` : r.found ? `green` : `amber`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export const SendingDomainsComponent = observer(function SendingDomainsComponent(): ReactElement {
  const vm = useSendingDomains()

  return (
    <PageScroll
      header={
        <>
          <PageHeaderComponent title="Sending Domains" />
          <div className="mb-4 flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="new-domain">Add a domain</Label>
              <Input
                id="new-domain"
                placeholder="mail.yourpractice.com"
                value={vm.newDomain}
                onChange={(e) => vm.setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === `Enter`) vm.onAddDomain().catch(logError)
                }}
              />
            </div>
            <Button onClick={() => vm.onAddDomain().catch(logError)} disabled={vm.adding || !vm.newDomain.trim()}>
              {vm.adding ? `Adding…` : `Add domain`}
            </Button>
          </div>
        </>
      }
    >
      {vm.loading ? (
        <p className="text-subtle">Loading…</p>
      ) : vm.domains.length === 0 ? (
        <p className="text-subtle">No sending domains yet. Add one above to send email from your own domain.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {vm.domains.map((domain) => (
            <div key={domain.id} className="rounded-md border border-default bg-surface p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-strong">{domain.domain}</span>
                  <Badge label={domain.status} color={STATUS_COLOR[domain.status] ?? `gray`} />
                  {domain.type === `trackable` && <Badge label="Trackable" color="blue" />}
                </div>
                <div className="flex items-center gap-2">
                  {domain.type === `custom` && domain.status !== `verified` && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => vm.onVerify(domain.id).catch(logError)}
                      disabled={vm.verifyingId === domain.id}
                    >
                      {vm.verifyingId === domain.id ? `Checking…` : `Check verification`}
                    </Button>
                  )}
                  <Button size="sm" variant="tertiary" onClick={() => vm.onDelete(domain.id).catch(logError)}>
                    Remove
                  </Button>
                </div>
              </div>
              {domain.type === `custom` && domain.status !== `verified` && <DnsRecords domain={domain} />}
            </div>
          ))}
        </div>
      )}
    </PageScroll>
  )
})
